import { apiService } from './apiService';
import { API_BASE_URL } from '../config/env';
import { ChatMessage } from '../models/clinicAgent';
import {
  Conversation,
  ConversationListFilter,
  ConversationPatient,
  ConversationsPage,
} from '../models/agentConversation';
import { ChatHistoryRow, chatRowToMessage } from './chatRows';

const BASE = `${API_BASE_URL}/clinic/agent/conversations/`;

// Coerce one Conversation from REST or a WS frame. Defensive on purpose: a
// missing optional field must not break the list.
export function toConversation(raw: any): Conversation {
  const p = raw?.patient;
  const patient: ConversationPatient | null =
    p && p.id != null
      ? { id: Number(p.id), userId: Number(p.userId ?? p.user_id), name: String(p.name ?? '') }
      : null;
  const preview = raw?.lastMessagePreview;
  const titleSource = raw?.titleSource;
  return {
    id: Number(raw?.id),
    kind: raw?.kind === 'patient' || (raw?.kind == null && patient) ? 'patient' : 'general',
    patient,
    title: String(raw?.title ?? ''),
    titleSource: titleSource === 'pending' || titleSource === 'user' ? titleSource : 'auto',
    createdAt: String(raw?.createdAt ?? ''),
    lastMessageAt: raw?.lastMessageAt ?? null,
    lastMessagePreview:
      preview && typeof preview.text === 'string'
        ? { role: preview.role === 'user' ? 'user' : 'assistant', text: preview.text }
        : null,
    pendingProposals: Number(raw?.pendingProposals) || 0,
    archived: Boolean(raw?.archived),
    isLegacy: Boolean(raw?.isLegacy),
    summaryUptoMessageId:
      raw?.summaryUptoMessageId == null ? null : Number(raw.summaryUptoMessageId),
  };
}

export interface ListConversationsOptions {
  filter?: ConversationListFilter;
  patientId?: number;
  q?: string;
  page?: number;
  perpage?: number;
}

export interface ConversationMessagesPage {
  messages: ChatMessage[]; // chronological
  hasMore: boolean;
  nextBeforeId: number | null;
}

export const agentConversationsService = {
  // Whether this backend has conversations. A 404 means it predates them, and
  // the panel keeps the single-thread chat. Other failures are re-thrown.
  isAvailable: async (): Promise<boolean> => {
    try {
      await apiService.get(`${BASE}?perpage=1`);
      return true;
    } catch (err: any) {
      if (err?.status === 404) return false;
      throw err;
    }
  },

  list: async (options: ListConversationsOptions = {}): Promise<ConversationsPage> => {
    const params = new URLSearchParams();
    const filter = options.filter ?? 'all';
    if (options.patientId != null) params.set('patient_id', String(options.patientId));
    else if (filter === 'general') params.set('kind', 'general');
    params.set('archived', filter === 'archived' ? 'true' : 'false');
    if (options.q?.trim()) params.set('q', options.q.trim());
    params.set('page', String(options.page ?? 1));
    params.set('perpage', String(options.perpage ?? 20));
    const data = await apiService.get(`${BASE}?${params.toString()}`);
    const results: unknown[] = Array.isArray(data?.results) ? data.results : [];
    return {
      conversations: results.map(toConversation),
      page: Number(data?.actualPage) || options.page || 1,
      totalPages: Number(data?.totalPages) || 1,
      count: Number(data?.count) || results.length,
    };
  },

  // The patient's most recent non-archived conversation, or null.
  newestForPatient: async (patientId: number): Promise<Conversation | null> => {
    const { conversations } = await agentConversationsService.list({ patientId, perpage: 1 });
    return conversations[0] ?? null;
  },

  get: async (id: number): Promise<Conversation> =>
    toConversation(await apiService.get(`${BASE}${id}/`)),

  update: async (id: number, patch: { title?: string; archived?: boolean }): Promise<Conversation> =>
    toConversation(await apiService.patch(`${BASE}${id}/`, patch)),

  remove: async (id: number): Promise<void> => {
    await apiService.delete(`${BASE}${id}/`);
  },

  // One page of transcript, newest first on the wire; returned chronological.
  getMessages: async (
    id: number,
    options: { beforeId?: number | null; limit?: number } = {},
  ): Promise<ConversationMessagesPage> => {
    const params = new URLSearchParams();
    if (options.beforeId != null) params.set('before_id', String(options.beforeId));
    params.set('limit', String(options.limit ?? 30));
    const data = await apiService.get(`${BASE}${id}/messages/?${params.toString()}`);
    const rows: ChatHistoryRow[] = Array.isArray(data?.results) ? data.results : [];
    return {
      messages: rows.map(chatRowToMessage).reverse(),
      hasMore: Boolean(data?.hasMore),
      nextBeforeId: data?.nextBeforeId == null ? null : Number(data.nextBeforeId),
    };
  },
};
